import { useState, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import type { SidebarProps } from './Sidebar';
import './BottomNavBar.css';

const MAX_VISIBLE = 4; // 4 items + More button = 5 slots

export function BottomNavBar({ navItems, onLogout }: SidebarProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleMenu = useCallback(() => setMenuOpen(prev => !prev), []);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  // Flatten: non-bottom items first, then bottom items. Group headers (no path)
  // collapse to their children so mobile only shows real routes.
  const flatten = (items: typeof navItems) =>
    items.flatMap(item => (item.path ? [item] : item.children ?? []));

  // `overflow`-marked items always live in the More menu (e.g. the student app's
  // You / Bookmarks / Insights). Everything else is a candidate tab, preserving
  // non-bottom-then-bottom order.
  const primaryItems = [
    ...flatten(navItems.filter(item => !item.overflow && !item.bottom)),
    ...flatten(navItems.filter(item => !item.overflow && item.bottom)),
  ];
  const forcedOverflow = [
    ...flatten(navItems.filter(item => item.overflow && !item.bottom)),
    ...flatten(navItems.filter(item => item.overflow && item.bottom)),
  ];

  // With no forced-overflow items and everything fitting, show all as tabs —
  // this is the teacher / admin apps' existing behavior. Otherwise the last slot
  // becomes More, holding both the excess primary tabs and the forced items.
  const fitsWithoutMore = forcedOverflow.length === 0 && primaryItems.length <= MAX_VISIBLE + 1;
  const visibleItems = fitsWithoutMore ? primaryItems : primaryItems.slice(0, MAX_VISIBLE);
  const overflowItems = fitsWithoutMore ? [] : [...primaryItems.slice(MAX_VISIBLE), ...forcedOverflow];
  const needsMore = overflowItems.length > 0;

  return (
    <nav className="bottom-nav">
      {visibleItems.map(item => (
        <NavLink
          key={item.path}
          to={item.path!}
          end={item.end}
          className={({ isActive }) => `bottom-nav-item ${isActive ? 'active' : ''}`}
        >
          <span className="bottom-nav-icon">{item.icon || item.label.charAt(0)}</span>
          <span className="bottom-nav-label">{item.label}</span>
          {item.badge ? <span className="bottom-nav-badge">{item.badge}</span> : null}
        </NavLink>
      ))}

      {needsMore && (
        <div className="bottom-nav-more-wrapper">
          <button
            className={`bottom-nav-more-btn ${menuOpen ? 'menu-open' : ''}`}
            onClick={toggleMenu}
            aria-label="More navigation options"
          >
            <span className="bottom-nav-icon">{menuOpen ? '\u2715' : '\u2026'}</span>
            <span className="bottom-nav-label">More</span>
          </button>

          {menuOpen && (
            <>
              <div className="bottom-nav-overlay" onClick={closeMenu} />
              <div className="bottom-nav-menu">
                {overflowItems.map(item => (
                  <NavLink
                    key={item.path}
                    to={item.path!}
                    end={item.end}
                    className={({ isActive }) =>
                      `bottom-nav-menu-item ${isActive ? 'active' : ''}`
                    }
                    onClick={closeMenu}
                  >
                    <span className="bottom-nav-menu-icon">{item.icon || item.label.charAt(0)}</span>
                    {item.label}
                    {item.badge ? <span className="bottom-nav-badge">{item.badge}</span> : null}
                  </NavLink>
                ))}

                {onLogout && (
                  <>
                    <div className="bottom-nav-menu-divider" />
                    <button
                      className="bottom-nav-menu-item logout"
                      onClick={() => { closeMenu(); onLogout(); }}
                    >
                      <span className="bottom-nav-menu-icon">{'\u2192'}</span>
                      Logout
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {!needsMore && !onLogout ? null : !needsMore && onLogout ? (
        <button className="bottom-nav-item" onClick={onLogout} aria-label="Logout">
          <span className="bottom-nav-icon">{'\u2192'}</span>
          <span className="bottom-nav-label">Logout</span>
        </button>
      ) : null}
    </nav>
  );
}
